import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { SocialLinks } from 'social-links';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { generateMediaURL } from 'helpers/generateMediaURL';
import { ImageComponent } from 'components/Image';
import { Button } from 'components/Button';
import { Arrow } from 'components/Arrow';
import { Link } from 'components/Link';
import arrowRight from 'assets/icons/arrow-right-background3_2-read-more.svg';
import styles from 'pages/AboutUs/Management/PersonCard/PersonCard.scss';

export const PersonCard = ({ person: { name, surname, role, socials, biography, photo }, isOpen, onToggle }) => {
  const socialLinks = new SocialLinks();
  const cardRef = useRef(null);
  const [pinnedTop, setPinnedTop] = useState(null);
  const [clipPercent, setClipPercent] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onToggle();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onToggle]);

  // The card overlays its grid row via `position: absolute`, which removes it from grid
  // flow. `.cardSlot` (its parent, see PersonCard.scss) exists purely to keep reserving
  // that grid cell regardless, so the grid doesn't collapse the row (or shrink the whole
  // section) once the card that used to size it is no longer in flow — this matters most
  // for a lone card in the last row, since removing it would otherwise leave that row with
  // no in-flow content at all. `top`/`left` are then measured relative to `.membersList`
  // (`.cardSlot`'s parent), which the card's `position: absolute` reaches past its own
  // (non-positioned) slot to use as its containing block.
  //
  // Capturing its offset within the grid before it opens and pinning `top` to that value
  // keeps it expanding in place, over its own row only, instead of jumping to the top of
  // the grid (the fallback "static position" for an absolutely-positioned grid item is the
  // whole grid, not its own row, when `top` is left unset).
  //
  // Width can't be transitioned smoothly either: the moment `position` flips from relative
  // to absolute, the browser resolves `width: 100%` instantly instead of interpolating it,
  // because the containing block itself changes in that same style pass. Clipping the
  // already-full-width box down to the card's original footprint with clip-path (which
  // resolves against a stable reference box throughout) and animating that to fully
  // revealed on the next frame gives the "grows out of its cell" effect without relying on
  // a width transition.
  const handleOpen = () => {
    const grid = cardRef.current?.parentElement?.parentElement;

    if (cardRef.current && grid) {
      const gridRect = grid.getBoundingClientRect();
      const cardRect = cardRef.current.getBoundingClientRect();
      setPinnedTop(cardRect.top - gridRect.top);
      setClipPercent(Math.max(0, (1 - cardRect.width / gridRect.width) * 100));
    }

    onToggle();
  };

  useEffect(() => {
    if (!isOpen) return;

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setClipPercent(0));
    });

    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  return (
    <div className={styles.cardSlot}>
      <div
        className={cn(styles.card, { [styles.open]: isOpen })}
        style={
          isOpen && pinnedTop !== null
            ? { '--pinned-top': `${pinnedTop}px`, '--pinned-clip': `${clipPercent ?? 0}%` }
            : undefined
        }
        ref={cardRef}
      >
        <div
          className={cn(styles.cardInner, {
            [styles.addMargin]: !socials && isOpen,
          })}
        >
          <ImageComponent
            className={styles.photo}
            src={generateMediaURL(photo?.url) || 'https://placehold.co/160'}
            alt={photo?.alternativeText || 'photo placeholder'}
            placeholder={photo?.placeholder}
          />
          <div className={styles.personInfo}>
            <p className={styles.name}>
              <span>{name}</span>
              <br />
              <span>{surname}</span>
            </p>
            <p className={styles.role}>{role}</p>
            {socials && isOpen && (
              <ul className={styles.socials}>
                {socials.map(({ id, url }) => {
                  const socialName = socialLinks.detectProfile(url);

                  if (socialName) {
                    return (
                      <li className={styles.socialWrapper} key={id}>
                        <Link className={cn(styles.social, styles[socialName])} path={url} external />
                      </li>
                    );
                  }
                })}
              </ul>
            )}
            {biography && !isOpen && (
              <Button className={styles.moreBtn} text='Read more' onClick={handleOpen} ghostStyle small>
                Read more <img src={arrowRight} alt='arrow right' />
              </Button>
            )}
          </div>
        </div>
        {biography && (
          <div className={cn(styles.biography, { [styles.open]: isOpen })} aria-hidden={!isOpen}>
            <div className={styles.biographyInner}>
              <Arrow className={styles.cross} onClick={onToggle} isOpen={isOpen} crossStyle tabIndex={isOpen ? 0 : -1} />
              <div className={styles.text}>
                <BlocksRenderer content={biography} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
